import React, { useState } from "react";
import { Stack, Button, Box, Typography } from "@mui/joy";
import PositionSelector from "../modals/ApplicantFilters/PositionSelector";
import SuperApplyToggle from "../modals/ApplicantFilters/SuperApplyToggle";
import RatingSlider from "./RatingSlider";
import SummarySection from "./SummarySection";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { updateApplicantThunk } from "../../redux/adminSlice";
import { useSelector } from "react-redux";
import PaymentFailure from "../../components/modals/PaymentFailure";
import PaymentSuccess from "../../components/modals/PaymentSuccess";
import SendCheckInModal from "./SendCheckInModal";
import { sendActiveCheckIn } from "../../apiconfig/api";
import { convertUTCToLocalDisplay } from "../../utils/helpers";

interface ProfileFormProps {
  returnToApplicantsUrl?: string;
}

const ProfileForm: React.FC<ProfileFormProps> = ({ returnToApplicantsUrl }) => {
  const applicantData = useSelector(
    (state: RootState) =>
      state?.admin?.applicantsData?.data?.user?.applicants?.[0]
  );

  const [selectedPositions, setSelectedPositions] = useState<string[]>(
    applicantData?.globalPositions
      ? applicantData.globalPositions.map((pos: { id: string }) => pos.id)
      : []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [rating, setRating] = useState(
    applicantData?.rating !== undefined && applicantData?.rating !== null
      ? applicantData.rating
      : 1
  );

  const [publish, setPublish] = useState(
    applicantData?.applicationStatus === "unpublish" ? false : true
  );

  const [isGloballyVisible, setIsGloballyVisible] = useState(
    applicantData?.isGloballyVisible !== undefined
      ? applicantData.isGloballyVisible
      : false
  );

  const [isActive, setIsActive] = useState(
    applicantData?.isActive !== undefined
      ? applicantData.isActive
      : true
  );

  const [summary, setSummary] = useState(
    applicantData?.summary || ""
  );

  const [showFailureModal, setShowFailureModal] = useState(false);
  const [failureMessage, setFailureMessage] = useState("");
  const [hasPaymentIntent, setHasPaymentIntent] = useState(false);
  const [showCheckInModal, setShowCheckInModal] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const handleSave = () => {
    if (!applicantData?.userId) return;
    setIsLoading(true);
    dispatch(
      updateApplicantThunk({
        applicantId: applicantData?.userId,
        data: {
          positions: selectedPositions || [],
          rating: rating,
          applicationStatus: publish ? "publish" : "unpublish",
          summary: summary,
          isGloballyVisible: isGloballyVisible,
          isActive: isActive,
        },
      })
    )
      .unwrap()
      .then(() => {
        setIsLoading(false);
        setHasPaymentIntent(true);
      })
      .catch((err: any) => {
        setIsLoading(false);
        setFailureMessage(err?.message || "Failed to update person");
        setShowFailureModal(true);
      });
  };

  const handleSendCheckIn = async (message: string) => {
    if (!applicantData?.userId) {
      throw new Error("Person ID not found");
    }
    
    await sendActiveCheckIn(applicantData.userId, { message });
  };

  const handleRetryPayment = () => {
    setShowFailureModal(false);
    handleSave();
  };

  const handleCancelPayment = () => {
    setShowFailureModal(false);
  };

  const handleCloseSuccessModal = () => {
    setHasPaymentIntent(false);
    
    // Use the passed returnToPeopleUrl or fallback to default
    const targetUrl = returnToApplicantsUrl || '/admin/applicants';
    navigate(targetUrl);
  };

  const userData = useSelector(
    (state: RootState) => state?.admin?.applicantsData?.data?.user
  );

  return (
    <Stack spacing={2}>
      <RatingSlider rating={rating} onChange={setRating} />

      <PositionSelector
        selectedPositions={selectedPositions}
        onSelectPosition={setSelectedPositions}
      />

      <SummarySection 
        value={summary} 
        onChange={setSummary}
        userUuid={applicantData?.userId || ''}
      />

      <SuperApplyToggle
        checked={isActive}
        onChange={(e) => setIsActive(e.target.checked)}
        text="Active"
      />

      {/* Last Active At Display with Check-In Button */}
      {applicantData?.lastActiveAt && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            bgcolor: '#F8F9FA',
            borderRadius: '12px',
          }}
        >
          <Box>
            <Typography level="body-sm" sx={{ color: '#7B7F85', fontWeight: 500 }}>
              Last known active at
            </Typography>
            <Typography level="body-md" sx={{ color: '#1A1B1D', fontWeight: 600 }}>
              {convertUTCToLocalDisplay(applicantData.lastActiveAt)}
            </Typography>
          </Box>
          <Button
            size="sm"
            variant="outlined"
            onClick={() => setShowCheckInModal(true)}
            sx={{
              color: '#2e2d7e',
              borderColor: '#2e2d7e',
              '&:hover': {
                backgroundColor: '#F8F9FA',
                borderColor: '#1e1d6e',
              },
            }}
          >
            Send check-in text
          </Button>
        </Box>
      )}

      <SuperApplyToggle
        checked={isGloballyVisible}
        onChange={(e) => setIsGloballyVisible(e.target.checked)}
        text="Globally Visible"
      />

      <SuperApplyToggle
        checked={publish}
        onChange={(e) => setPublish(e.target.checked)}
        text="Publish"
      />

      <Button
        loading={isLoading}
        fullWidth
        variant="solid"
        color="primary"
        onClick={handleSave}
      >
        Save
      </Button>

      <PaymentFailure
        open={showFailureModal}
        onRetry={handleRetryPayment}
        onCancel={handleCancelPayment}
        message={failureMessage}
        title="Something went wrong"
        subtitle="The person couldn't be updated."
        retryButtonText="Try Again"
        cancelButtonText="Close"
      />

      <PaymentSuccess
        open={hasPaymentIntent}
        onClose={handleCloseSuccessModal}
        title="Successfully Saved!"
        subtitle="The person has been updated successfully."
        buttonText="Back to People"
      />

      <SendCheckInModal
        open={showCheckInModal}
        onClose={() => setShowCheckInModal(false)}
        onSend={handleSendCheckIn}
        applicantName={userData?.fullName || ''}
        applicantId={applicantData?.uuid || ''}
      />
    </Stack>
  );
};

export default ProfileForm;
